import {
	FilterKey,
	IFilterPayload,
	IFilterProvider,
	ICurrencyFilterProvider,
	ILocationFilterProvider,
	ISuggestionPayload
} from '@studyportals/search-filters/server-side';
import {FilterOptionInformation} from '@studyportals/search-filters'
import store from '@/study-search/store';
import { FacetValue, ParameterValue } from '@/study-search/domain';
import { ParameterType } from '@/study-search/enums';
import { EventsSender } from '@/study-search/infrastructure/events';
import globals from '@/study-search/utils/globals';
import { AreaFilterDefinition } from '@/study-search/models/definition';
import Tracking from '@/study-search/infrastructure/tracking/Tracking';
import { Product } from '@studyportals/datalake-event-tracker';
import SearchResultsPresenter from '@/study-search/presenters/SearchResultsPresenter';
import getters from '@/study-search/store/getters';
import cloneDeep from 'lodash/cloneDeep';
import { ParameterService } from '@/study-search/infrastructure/ParameterService';
import { Filter } from '@/study-search/models/filter';

export default class FilterProvider implements IFilterProvider, ILocationFilterProvider, ICurrencyFilterProvider {
	private static readonly Instance = new FilterProvider();

	public static get instance(): IFilterProvider {
		return FilterProvider.Instance;
	}

	trackedProduct = Product.SEARCH_PAGE;

	displayFacets = true;

	includeContinents = true;

	applySuggestion = async (payload: ISuggestionPayload): Promise<void> => {
		await store.actions.applyFilterSuggestion(payload);
	};

	getCountOfResultsForSuggestion = async (payload: ISuggestionPayload): Promise<number> => {

		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		const filtersCopy: Filter[] = cloneDeep(store.getters.getFilters());

		payload.toDeselect.forEach((key) => {
			const filter: Filter = filtersCopy.filter((_) => _.key === key)[0];

			if (filter !== undefined && filter.key !== FilterKey.CAMPAIGN_TYPE) {
				filter.emptyFilter();
			}
		});

		payload.toSelect.forEach((item: IFilterPayload) => {
			const filter: Filter = filtersCopy.filter((_) => _.key === item.key)[0];

			if (filter !== undefined) {
				filter.processValue(item.value);
			}
		});

		const parameterService = new ParameterService();
		const parameters: ParameterValue[] = parameterService.getParameters(filtersCopy);
		const portalType = getters.getPortalType();
		const searchResult = await SearchResultsPresenter.retrieveSearchResults(parameters, portalType);
		return searchResult.count;
	};

	getFilterKeySelection = (): readonly FilterKey[] => {
		const allFilters = store.getters.getFilters();
		const filterSelection = allFilters.filter((filter) => filter.values.length > 0);
		return filterSelection.map((filter) => filter.key);
	};

	getFilterSelection = (key: FilterKey): string[] => store.getters.getFilterValues(key.toString() as ParameterType);

	isFilterCollapsed = (key: FilterKey): boolean => store.getters.getFilterCollapseOption(key.toString() as ParameterType);

	isFilterRequired = (key: FilterKey): boolean => {
		return store.getters.getPortalDefinition().isFilterRequired(key.toString() as ParameterType);
	};

	getFacet = (key: FilterKey, optionValue: string): number | null => {
		const filterFacets = store.getters.getFacets(key.toString() as ParameterType);

		if (filterFacets == null || filterFacets.values == null) {
			return 0;
		}

		const facet: FacetValue | undefined = filterFacets.values.find((_) => _.identity === optionValue);
		return facet === undefined ? 0 : facet.count;
	};

	getFilterOptionInformation = (key: FilterKey): FilterOptionInformation[] => store.getters.getFilterOptionInformation(key);

	getFilterOptionInformationById = (key: FilterKey, id: string): FilterOptionInformation | null =>
		store.getters.getFilterOptionInformationById(key, id);

	toggleFilterExpandability = (key: FilterKey): void => store.mutations.toggleFilter(key.toString() as ParameterType);

	processFilterSelection = async (payload: IFilterPayload): Promise<void> => await store.actions.processFilterSelection(payload);

	processFiltersSelection = async (payload: IFilterPayload[]): Promise<void> => await store.actions.processFilters(payload);

	clearFilter = async (key: FilterKey): Promise<void> => await store.actions.clearFilterFor(key);

	clearFilters = async (keys: FilterKey[]): Promise<void> => await store.actions.clearFiltersFor(keys);

	clearSelection = async (payload: IFilterPayload[]): Promise<void> => await store.actions.clearFilterValues(payload);

	getCurrency = (): string => store.getters.getCurrency();

	requestCurrencyChange = (): void => EventsSender.sendChangeCurrencyEvent();

	currencyConvert = async (originalAmount: number, originalCurrency: string, targetCurrency?: string): Promise<number> =>
		globals.currencyConversionService.convert(originalAmount, originalCurrency, targetCurrency);

	trackCustomClickEvent = (label: string, property: string): void => Tracking.trackCustomClickEvent(label, property);

	getAreDependenciesLoaded = (): boolean => store.getters.getAreDependenciesLoaded();

	identifyTopCountries = (): string[] => store.getters.getTopCountryIds();

	identifyCountryFor = (areaId: string): string => AreaFilterDefinition.getCountryId(areaId);

	identifyAreasFor = (countryId: string): string[] => AreaFilterDefinition.getAreas(countryId);
}
